import { useContext, useEffect, useState } from 'react'
import {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  useForm,
  useWatch
} from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import {
  useGenerateArticleMutation,
  useGenerateOutlineMutation
} from '@/common/api/modules/article'
import { useCreateArticleMutation } from '@/common/api/modules/articles'
import { useGscSitesApi, useGscStatusApi } from '@/common/api/modules/gsc'
import { useAnalysisTargetListApi } from '@/common/api/modules/mediaAnalysisTarget'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import {
  ARTICLE_SLUG_MAX_LENGTH,
  ARTICLE_TITLE_MAX_LENGTH,
  PERSONA_PLACEHOLDER,
  TARGET_CHAR_COUNT_CHOICES,
  TONE_RADIO_CHOICES,
  articleStatusLabel
} from '@/common/app/modules/media/article'
import { analysisTargetPath, mediaGscLinkagePath } from '@/common/app/path'
import { registerNumberOption } from '@/common/form/register'
import { StringLength } from '@/common/form/schema'
import { ARTICLE_PERSONA_MAX_LENGTH } from '@/common/model/media/article'
import { GeneratedArticle } from '@/common/model/media/articleCreator'
import { handleSmoothScroll } from '@/common/util/scroll'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import AiButton from '@/components/view/atoms/button/AiButton'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import TextButton from '@/components/view/atoms/button/TextButton'
import CheckBox from '@/components/view/atoms/checkbox/CheckBox'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import Loading from '@/components/view/atoms/icon/Loading'
import Selector, { SelectorSize } from '@/components/view/atoms/selector/Selector'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import TextField, { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import AiInsideArea from '@/components/view/layouts/area/AiInsideArea'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import Line from '@/components/view/layouts/area/Line'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content, { ContentType } from '@/components/view/layouts/page/Content'
import ContentSideFrame from '@/components/view/layouts/page/ContentSideFrame'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import Side from '@/components/view/layouts/page/Side'
import SerpPreview from '@/components/view/molecules/article/SerpPreview'
import { MessageType } from '@/components/view/molecules/bar/MessageBar'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import TooltipWrapper from '@/components/view/utils/tooltip/TooltipWrapper'

import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormRadioButton from '@/components/container/form/widgets/FormRadioButton'
import FormSelector from '@/components/container/form/widgets/FormSelector'
import FormTextArea from '@/components/container/form/widgets/FormTextArea'
import FormTextField from '@/components/container/form/widgets/FormTextField'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'
import { GscKeywordAnalysisModalContext } from '@/components/container/modal/article/GscKeywordAnalysisModalProvider'
import { KeywordHistoryResultModalContext } from '@/components/container/modal/article/KeywordHistoryResultModalProvider'
import { KeywordSuggestionModalContext } from '@/components/container/modal/article/KeywordSuggestionModalProvider'
import AnalysisTargetSummary from '@/components/container/modules/media/analysisTarget/AnalysisTargetSummary'
import KeywordAnalysisHistoryList from '@/components/container/modules/media/article/KeywordAnalysisHistoryList'
import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

import {
  ArticleCreateSchemaType,
  DEFAULT_ARTICLE_CREATE_SCHEMA,
  applyGeneratedArticleToSchema,
  applyGeneratedOutlineToSchema,
  articleCreateSchema,
  convertSchemaToCreateVars
} from './articleCreateSchema'

// GSCキーワード実績分析の抽出期間（backend/routers/gsc_integration.py の days パラメータ、1〜90日の範囲に準拠）
const GSC_KEYWORD_ANALYSIS_DAYS_CHOICES = [
  { name: '過去7日間', value: 7 },
  { name: '過去28日間', value: 28 },
  { name: '過去90日間', value: 90 }
]

interface KeywordProps {
  siteId: number
  keyword: string
  register: UseFormRegister<ArticleCreateSchemaType>
  setValue: UseFormSetValue<ArticleCreateSchemaType>
  getValues: UseFormGetValues<ArticleCreateSchemaType>
  errors: FieldErrors<ArticleCreateSchemaType>
  isOpening: boolean
  onToggleOpen(): void
  onOutlineGenerated(): void
  onArticleAutoGenerated(article: GeneratedArticle): void
  onSkip(): void
  onSkipToBody(): void
}

const ArticleCreateFormKeyword = ({
  siteId,
  keyword,
  register,
  setValue,
  getValues,
  errors,
  isOpening,
  onToggleOpen,
  onOutlineGenerated,
  onArticleAutoGenerated,
  onSkip,
  onSkipToBody
}: KeywordProps) => {
  const [mode, setMode] = useState<'ai' | 'gsc' | 'history' | undefined>(undefined)
  const [selectedTargetId, setSelectedTargetId] = useState<number | ''>('')
  // 記事テーマ：キーワード分析実行への入力専用（記事DBには保存しない）
  const [articleTheme, setArticleTheme] = useState('')
  const [gscSiteUrl, setGscSiteUrl] = useState('')
  const [gscDays, setGscDays] = useState(28)

  const { addMessage } = useContext(ToastMessageContext)
  const { showModal: showKeywordSuggestion } = useContext(KeywordSuggestionModalContext)
  const { showModal: showGscKeywordAnalysis } = useContext(GscKeywordAnalysisModalContext)
  const { showModal: showKeywordHistoryResult } = useContext(KeywordHistoryResultModalContext)

  const { data: gscStatus } = useGscStatusApi({ site_id: siteId })
  const { data: gscSites } = useGscSitesApi(
    gscStatus?.is_connected ? { site_id: siteId } : undefined
  )

  const { data: analysisTargets } = useAnalysisTargetListApi({ site_id: siteId })
  const selectedTarget = analysisTargets?.find((t) => t.id === selectedTargetId)

  useEffect(() => {
    if (analysisTargets && analysisTargets.length > 0 && selectedTargetId === '') {
      setSelectedTargetId(analysisTargets[0]?.id ?? '')
    }
  }, [analysisTargets])

  useEffect(() => {
    if (gscSites && gscSites.sites.length > 0 && !gscSiteUrl) {
      setGscSiteUrl(gscSites.sites[0]?.site_url ?? '')
    }
  }, [gscSites])

  const applyKeyword = (kw: string) => {
    setValue('keyword', kw, { shouldDirty: true })
    setMode(undefined)
  }

  const { trigger: generateOutline, isMutating: generatingOutline } = useGenerateOutlineMutation({
    onSuccess: (res) => {
      const applied = applyGeneratedOutlineToSchema(getValues(), res.outline)
      setValue('headingOutline', applied.headingOutline, { shouldDirty: true })
      setValue('tone', applied.tone, { shouldDirty: true })
      setValue('targetCharCount', applied.targetCharCount, { shouldDirty: true })
      setValue('titleCandidate', applied.titleCandidate, { shouldDirty: true })
      setValue('metaDescriptionCandidate', applied.metaDescriptionCandidate, { shouldDirty: true })
      setValue('internalLinkCandidates', applied.internalLinkCandidates, { shouldDirty: true })
      onOutlineGenerated()
      onSkip()
    },
    onError: () =>
      addMessage({ text: '記事構成案の生成に失敗しました', type: MessageType.CRITICAL })
  })

  const { trigger: generateArticle, isMutating: generatingArticle } = useGenerateArticleMutation({
    onSuccess: (res) => {
      onArticleAutoGenerated(res.article)
      onSkipToBody()
    },
    onError: () => addMessage({ text: 'AI記事生成に失敗しました', type: MessageType.CRITICAL })
  })

  const handleGenerateOutline = () => {
    if (!keyword.trim()) return
    generateOutline({ site_id: siteId, keyword: keyword.trim() }).catch(() => undefined)
  }

  const handleAutoGenerateArticle = () => {
    if (!keyword.trim()) return
    generateArticle({
      site_id: siteId,
      keyword: keyword.trim(),
      persona: getValues('persona').trim() || undefined
    }).catch(() => undefined)
  }

  return (
    <Area id='step-keyword' bottomMargin={24}>
      <Heading
        subText='記事に含めるべきキーワードを複数入力してください（大体のテーマでOK）。キーワード分析からキーワードを選定することも可能です。'
        onOpenCloseClick={onToggleOpen}
        isOpening={isOpening}
      >
        <ItemAlign>
          <Badge badgeType={BadgeType.SQUARE_PRIMARY}>STEP 01</Badge>
          キーワード選定
        </ItemAlign>
      </Heading>
      <AreaContent isOpening={isOpening}>
        <div className='flex flex-col gap-4'>
          <FormRow direction='vertical' size={TextBoxSize.FULL}>
            <FormRow align='center'>
              <FormLabel>GSC連携</FormLabel>
              {gscStatus ? (
                <>
                  <Badge
                    badgeType={gscStatus?.is_connected ? BadgeType.SUCCESS : BadgeType.SECONDARY}
                  >
                    {gscStatus.is_connected ? '連携済み' : '未連携'}
                  </Badge>
                  {!gscStatus.is_connected && (
                    <TextButton link={mediaGscLinkagePath(siteId)} arrow>
                      GSC連携する
                    </TextButton>
                  )}
                </>
              ) : (
                <Loading />
              )}
            </FormRow>
            <FormRow justify='between' align='center'>
              <FormRow>
                <AiButton onClick={() => setMode('ai')} disabled={mode == 'ai'}>
                  AIでキーワード選定
                </AiButton>
                <TooltipWrapper text='GSC連携をしてください' off={gscStatus?.is_connected}>
                  <Button
                    color={ButtonColor.SECONDARY}
                    onClick={() => setMode('gsc')}
                    disabled={mode == 'gsc' || gscStatus?.is_connected === false}
                  >
                    GSCから抽出
                  </Button>
                </TooltipWrapper>
                <Button
                  color={ButtonColor.SECONDARY}
                  onClick={() => setMode('history')}
                  disabled={mode == 'history'}
                >
                  分析履歴から選定する
                </Button>
              </FormRow>
              <TextButton
                onClick={() => setMode(undefined)}
                arrow
                nowrap
                disabled={mode == undefined}
              >
                スキップする（手動入力）
              </TextButton>
            </FormRow>
            {mode == 'ai' && (
              <AiInsideArea border noFill>
                <div className='flex w-full flex-col gap-4 p-3'>
                  <FormRow>
                    <FormLabel required>記事テーマ</FormLabel>
                    <FormRow direction='vertical'>
                      <TextField
                        name='articleTheme'
                        size={TextBoxSize.FULL}
                        value={articleTheme}
                        onChange={(e) => setArticleTheme(e.target.value)}
                        placeholder='乾燥肌のスキンケア方法'
                      />
                      <Text size={12} color={TextColor.SECONDARY}>
                        分析したいテーマを入力してください（キーワード分析実行にのみ使用します）。
                      </Text>
                    </FormRow>
                  </FormRow>
                  <FormRow>
                    <FormLabel>分析対象サイト名</FormLabel>
                    <FormRow direction='vertical'>
                      <div className='flex items-center gap-3'>
                        <Selector
                          name='analysisTarget'
                          size={SelectorSize.MEDIUM}
                          choices={(analysisTargets ?? []).map((t) => ({
                            name: t.name,
                            value: t.id
                          }))}
                          value={selectedTargetId}
                          onChange={(e) =>
                            setSelectedTargetId(e.target.value ? Number(e.target.value) : '')
                          }
                        />
                        <TextButton link={analysisTargetPath(siteId)} newTab>
                          分析対象管理
                        </TextButton>
                      </div>
                      {selectedTarget && <AnalysisTargetSummary target={selectedTarget} />}
                    </FormRow>
                  </FormRow>
                  <ItemAlign justify='center'>
                    <AiButton
                      disabled={!articleTheme.trim()}
                      onClick={() =>
                        showKeywordSuggestion({
                          siteId,
                          keyword: articleTheme.trim(),
                          siteUrl: selectedTarget?.site_url,
                          onApply: applyKeyword
                        })
                      }
                      size='large'
                    >
                      キーワード分析実行
                    </AiButton>
                  </ItemAlign>
                </div>
              </AiInsideArea>
            )}
            {mode == 'gsc' && (
              <InsideArea color={BgColor.WHITE} border>
                <ItemAlign gapY={8} align='center' justify='center' direction='vertical'>
                  <FormRow align='center'>
                    <FormLabel>分析対象サイトURL</FormLabel>
                    <Selector
                      name='gscSiteUrl'
                      size={SelectorSize.MEDIUM}
                      choices={(gscSites?.sites ?? []).map((s) => ({
                        name: s.site_url,
                        value: s.site_url
                      }))}
                      value={gscSiteUrl}
                      onChange={(e) => setGscSiteUrl(e.target.value)}
                    />
                  </FormRow>
                  <FormRow align='center'>
                    <FormLabel>抽出期間</FormLabel>
                    <Selector
                      name='gscDays'
                      size={SelectorSize.MEDIUM}
                      choices={GSC_KEYWORD_ANALYSIS_DAYS_CHOICES}
                      value={gscDays}
                      onChange={(e) => setGscDays(Number(e.target.value))}
                    />
                  </FormRow>
                  <ItemAlign justify='center'>
                    <Button
                      disabled={!gscStatus?.is_connected || !gscSiteUrl}
                      onClick={() =>
                        showGscKeywordAnalysis({
                          siteId,
                          keyword,
                          siteUrl: gscSiteUrl,
                          days: gscDays,
                          onApply: applyKeyword
                        })
                      }
                      size='large'
                    >
                      キーワード抽出実行
                    </Button>
                  </ItemAlign>
                </ItemAlign>
              </InsideArea>
            )}
            {mode == 'history' && (
              <>
                <KeywordAnalysisHistoryList
                  siteId={siteId}
                  onApply={applyKeyword}
                  onViewResult={(historyId) =>
                    showKeywordHistoryResult({ siteId, historyId, onApply: applyKeyword })
                  }
                />
              </>
            )}
            <FormRow>
              <FormLabel required>キーワード</FormLabel>
              <FormRow direction='vertical'>
                <FormTextField
                  register={register('keyword')}
                  error={!!errors.keyword}
                  size={TextBoxSize.FULL}
                  placeholder='乾燥肌 スキンケア'
                />
                <FormError message={errors.keyword?.message} />
              </FormRow>
            </FormRow>
          </FormRow>
          <ItemAlign gapY={8} align='center' justify='center' direction='vertical'>
            <ItemAlign gapX={8} align='center' justify='center'>
              <AiButton
                size='large'
                disabled={!keyword.trim()}
                loading={generatingOutline}
                onClick={handleGenerateOutline}
              >
                {generatingOutline ? '構成案を生成中' : '記事構成案を生成する'}
              </AiButton>
              <AiButton
                size='large'
                disabled={!keyword.trim()}
                loading={generatingArticle}
                onClick={handleAutoGenerateArticle}
              >
                {generatingArticle ? '記事を生成中' : 'キーワードから記事を自動作成する'}
              </AiButton>
            </ItemAlign>
            <ItemAlign justify='center'>
              <TextButton onClick={onSkip}>スキップする（手動で構成案を入力する）</TextButton>
            </ItemAlign>
          </ItemAlign>
        </div>
      </AreaContent>
    </Area>
  )
}

interface OutlineProps {
  siteId: number
  register: UseFormRegister<ArticleCreateSchemaType>
  control: Control<ArticleCreateSchemaType>
  setValue: UseFormSetValue<ArticleCreateSchemaType>
  getValues: UseFormGetValues<ArticleCreateSchemaType>
  errors: FieldErrors<ArticleCreateSchemaType>
  isOpening: boolean
  onToggleOpen(): void
  onArticleGenerated(article: GeneratedArticle): void
  onSkip(): void
}

// STEP02: 構成案作成（本文はまだ生成しない。見出し構成案・トーン・目標文字数・タイトル案・SEOディスクリプション案・内部リンク候補）
const ArticleCreateFormOutline = ({
  siteId,
  register,
  control,
  setValue,
  getValues,
  errors,
  isOpening,
  onToggleOpen,
  onArticleGenerated,
  onSkip
}: OutlineProps) => {
  const { addMessage } = useContext(ToastMessageContext)

  const titleCandidate = useWatch({ control, name: 'titleCandidate' })
  const internalLinkCandidates = useWatch({ control, name: 'internalLinkCandidates' })

  const { trigger: generateArticle, isMutating: generating } = useGenerateArticleMutation({
    onSuccess: (res) => {
      onArticleGenerated(res.article)
    },
    onError: () => addMessage({ text: 'AI記事生成に失敗しました', type: MessageType.CRITICAL })
  })

  const handleGenerateArticle = () => {
    const data = getValues()
    if (!data.keyword.trim()) {
      addMessage({ text: 'キーワードを入力してください', type: MessageType.CRITICAL })
      return
    }
    generateArticle({
      site_id: siteId,
      keyword: data.keyword.trim(),
      outline: data.headingOutline.trim()
        ? data.headingOutline
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean)
        : undefined,
      tone: data.tone,
      persona: data.persona.trim() || undefined,
      target_length: data.targetCharCount,
      title_candidates: titleCandidate.split(' '),
      internal_link_candidates: data.internalLinkCandidates
        .filter((c) => c.selected)
        .map((c) => ({ title: c.title, slug: c.slug }))
    }).catch(() => undefined)
  }

  const toggleInternalLinkCandidate = (index: number, selected: boolean) => {
    const updated = [...internalLinkCandidates]
    updated[index] = { ...updated[index], selected }
    setValue('internalLinkCandidates', updated, { shouldDirty: true })
  }

  return (
    <Area id='step-outline' bottomMargin={16}>
      <Heading
        subText='AIが生成した構成案（タイトル案・見出し構成案・内部リンク候補）を確認・編集してから本文を生成できます。'
        onOpenCloseClick={onToggleOpen}
        isOpening={isOpening}
      >
        <ItemAlign>
          <Badge badgeType={BadgeType.SQUARE_PRIMARY}>STEP 02</Badge>
          構成案作成
        </ItemAlign>
      </Heading>
      <AreaContent isOpening={isOpening}>
        <div className='flex flex-col gap-4'>
          <FormRow direction='vertical' align='center'>
            <FormRow>
              <FormLabel>ページタイトル案</FormLabel>
              <TooltipWrapper
                text='SEO観点では30〜40文字程度が推奨です。'
                off={titleCandidate.length < ARTICLE_TITLE_MAX_LENGTH - 10}
                className='flex-1'
              >
                <FormTextField
                  register={register('titleCandidate')}
                  size={TextBoxSize.FULL}
                  suffix={titleCandidate.length + '/' + ARTICLE_TITLE_MAX_LENGTH}
                />
              </TooltipWrapper>
            </FormRow>

            <FormRow>
              <FormLabel>SEOディスクリプション案</FormLabel>
              <TooltipWrapper
                text='スマホ重視なら70文字前後、PC重視なら100〜120文字が適切です。'
                className='flex-1'
              >
                <FormTextArea
                  name='metaDescriptionCandidate'
                  register={register('metaDescriptionCandidate')}
                  control={control}
                  counter
                  maxLength={160}
                  minRows={3}
                />
              </TooltipWrapper>
            </FormRow>

            <FormRow>
              <FormLabel subText='1行1見出し、空欄の場合はAIが生成'>見出し構成案</FormLabel>
              <FormTextArea
                name='headingOutline'
                register={register('headingOutline')}
                control={control}
                error={!!errors.headingOutline}
                minRows={4}
              />
            </FormRow>

            <FormRow>
              <FormLabel>トーン</FormLabel>
              <FormRadioButton
                control={control}
                name='tone'
                choices={TONE_RADIO_CHOICES}
                input
                direction='horizon'
              />
            </FormRow>

            <FormRow>
              <FormLabel subText='記事の想定される読み手。AI記事生成・AIリライトのプロンプトへ反映されます'>
                ペルソナ
              </FormLabel>
              <FormTextArea
                name='persona'
                register={register('persona')}
                control={control}
                error={!!errors.persona}
                maxLength={ARTICLE_PERSONA_MAX_LENGTH}
                placeholder={PERSONA_PLACEHOLDER}
                minRows={3}
              />
            </FormRow>

            <FormRow>
              <FormLabel>目標文字数</FormLabel>
              <FormSelector
                register={register('targetCharCount', registerNumberOption)}
                choices={TARGET_CHAR_COUNT_CHOICES}
                size={SelectorSize.MEDIUM}
              />
            </FormRow>

            {internalLinkCandidates.length > 0 && (
              <FormRow>
                <FormLabel subText='AI記事構成案作成で選定された関連記事です。不要な場合はチェックを外してください。'>
                  内部リンク候補
                </FormLabel>
                <ItemAlign>
                  {internalLinkCandidates.map((c, i) => (
                    <ItemAlign key={c.article_proposal_id} gapX={8} align='center'>
                      <CheckBox
                        name={`internal-link-${c.article_proposal_id}`}
                        label={c.title}
                        checked={c.selected}
                        onChange={(v) => toggleInternalLinkCandidate(i, v)}
                      />
                      <Badge badgeType={BadgeType.DEFAULT} size={10}>
                        {articleStatusLabel(c.status)}
                      </Badge>
                    </ItemAlign>
                  ))}
                </ItemAlign>
              </FormRow>
            )}
          </FormRow>
          <ItemAlign direction='vertical' justify='center' gapY={8} align='center'>
            <AiButton size='large' onClick={handleGenerateArticle} loading={generating}>
              {generating ? '記事を生成中' : 'この内容で記事を自動作成する'}
            </AiButton>
            <TextButton onClick={onSkip}>スキップする（手動で本文入力する）</TextButton>
          </ItemAlign>
        </div>
      </AreaContent>
    </Area>
  )
}

interface BodyProps {
  register: UseFormRegister<ArticleCreateSchemaType>
  control: Control<ArticleCreateSchemaType>
  errors: FieldErrors<ArticleCreateSchemaType>
  isOpening: boolean
  onToggleOpen(): void
}

// STEP03: 記事本文作成（ページタイトル・コンテンツエリア上部/下部・記事URL）
const ArticleCreateFormBody = ({
  register,
  control,
  errors,
  isOpening,
  onToggleOpen
}: BodyProps) => {
  const slug = useWatch({ control, name: 'slug' })
  const pageTitle = useWatch({ control, name: 'pageTitle' })

  return (
    <Area id='step-body' bottomMargin={16}>
      <Heading
        subText='AIに任せてSEO最適化された記事を丸ごと生成することも、手動で記事を執筆することもできます。'
        onOpenCloseClick={onToggleOpen}
        isOpening={isOpening}
      >
        <ItemAlign>
          <Badge badgeType={BadgeType.SQUARE_PRIMARY}>STEP 03</Badge>
          記事本文作成
        </ItemAlign>
      </Heading>
      <AreaContent isOpening={isOpening}>
        <FormRow direction='vertical' size={TextBoxSize.FULL}>
          <FormRow gap={4} size={TextBoxSize.FULL}>
            <FormLabel>ページタイトル</FormLabel>
            <FormTextField
              register={register('pageTitle')}
              size={TextBoxSize.FULL}
              suffix={pageTitle.length + '/' + ARTICLE_TITLE_MAX_LENGTH}
            />
          </FormRow>
          <Line />
          <FormRow direction='vertical' gap={4} size={TextBoxSize.FULL}>
            <FormLabel>コンテンツエリア上部</FormLabel>
            <FormTextArea
              name='bodyHtml'
              register={register('bodyHtml')}
              control={control}
              minRows={8}
              maxLength={StringLength.HTML_MAX}
            />
          </FormRow>
          <Line />
          <FormRow direction='vertical' gap={4} size={TextBoxSize.FULL}>
            <FormLabel>コンテンツエリア下部</FormLabel>
            <FormTextArea
              name='bodyHtmlBottom'
              register={register('bodyHtmlBottom')}
              control={control}
              minRows={8}
              maxLength={StringLength.HTML_MAX}
            />
          </FormRow>
          <Line />
          <FormRow gap={4} size={TextBoxSize.FULL}>
            <FormLabel>記事URL（スラッグ）</FormLabel>
            <FormTextField
              register={register('slug')}
              error={!!errors.slug}
              size={TextBoxSize.FULL}
              suffix={slug.length + '/' + ARTICLE_SLUG_MAX_LENGTH}
            />
            <FormError message={errors.slug?.message} />
          </FormRow>
        </FormRow>
      </AreaContent>
    </Area>
  )
}

interface SeoProps {
  register: UseFormRegister<ArticleCreateSchemaType>
  control: Control<ArticleCreateSchemaType>
  errors: FieldErrors<ArticleCreateSchemaType>
  isOpening: boolean
  onToggleOpen(): void
}

// STEP04: SEO情報チェック（HTMLページタイトル・SEOディスクリプション・SERPプレビュー）
const ArticleCreateFormSeo = ({ register, control, errors, isOpening, onToggleOpen }: SeoProps) => {
  const title = useWatch({ control, name: 'title' })
  const metaDescription = useWatch({ control, name: 'metaDescription' })
  const slug = useWatch({ control, name: 'slug' })

  return (
    <Area id='step-seo' bottomMargin={16}>
      <Heading
        subText='検索結果でのタイトル・スニペットの見え方を確認できます。'
        onOpenCloseClick={onToggleOpen}
        isOpening={isOpening}
      >
        <ItemAlign>
          <Badge badgeType={BadgeType.SQUARE_PRIMARY}>STEP 04</Badge>
          SEO情報チェック
        </ItemAlign>
      </Heading>
      <AreaContent isOpening={isOpening}>
        <FormRow direction='vertical' size={TextBoxSize.FULL}>
          <FormRow>
            <FormLabel>HTMLページタイトル</FormLabel>
            <TooltipWrapper
              text='SEO観点では30〜40文字程度が推奨です。'
              off={title.length < ARTICLE_TITLE_MAX_LENGTH - 10}
              className='flex-1'
            >
              <FormTextField
                register={register('title')}
                error={!!errors.title}
                size={TextBoxSize.FULL}
                placeholder='乾燥肌に悩む方必見！保湿スキンケアの選び方完全ガイド'
                suffix={title.length + '/' + ARTICLE_TITLE_MAX_LENGTH}
              />
            </TooltipWrapper>
            <FormError message={errors.title?.message} />
          </FormRow>

          <FormRow>
            <FormLabel help='スマホ重視なら70文字前後、PC重視なら100〜120文字が適切です。'>
              SEOディスクリプション
            </FormLabel>
            <FormTextArea
              name='metaDescription'
              register={register('metaDescription')}
              control={control}
              error={!!errors.metaDescription}
              counter
              maxLength={160}
              minRows={3}
            />
          </FormRow>

          <FormRow>
            <FormLabel fixWidth={null}>SERPプレビュー</FormLabel>
            <SerpPreview slug={slug} pageTitle={title} metaDescription={metaDescription} />
          </FormRow>
        </FormRow>
      </AreaContent>
    </Area>
  )
}

const CREATE_STEPS = [
  { key: 'keyword', label: 'STEP 01', title: 'キーワード選定' },
  { key: 'outline', label: 'STEP 02', title: '構成案作成' },
  { key: 'body', label: 'STEP 03', title: '記事本文作成' },
  { key: 'seo', label: 'STEP 04', title: 'SEO情報チェック' }
] as const

// 記事作成フォームの全体進捗（各STEPへのアンカーリンク。SetupGuidePageの「全体進捗」と同様のパターン）
const ArticleCreateProgress = () => (
  <Area>
    <Heading>全体進捗</Heading>
    <AreaContent>
      <div className='flex flex-col gap-1.5'>
        {CREATE_STEPS.map((step) => (
          <a
            key={step.key}
            href={`#step-${step.key}`}
            className='border-secondary inline-flex w-full items-center gap-2 rounded-xl border px-4 py-3 no-underline transition-all'
            onClick={handleSmoothScroll}
          >
            <Badge badgeType={BadgeType.SQUARE_PRIMARY} size={12}>
              {step.label}
            </Badge>
            <Text weight='bold' color={TextColor.PRIMARY}>
              {step.title}
            </Text>
          </a>
        ))}
      </div>
    </AreaContent>
  </Area>
)

interface Props {
  siteId: number
  defaultValues?: Partial<ArticleCreateSchemaType>
  onCreated(articleProposalId: number): void
  // 推奨アクションから遷移してきた場合の起点アクションID（保存時に記事へ紐づける）
  sourceActionId?: number | null
}

// 記事作成フォーム（キーワード選定 → 構成案作成 → 本文作成＋SEO情報チェック）
const ArticleCreateForm = ({ siteId, defaultValues, onCreated, sourceActionId }: Props) => {
  const initialValues = { ...DEFAULT_ARTICLE_CREATE_SCHEMA, ...defaultValues }

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    formState: { errors, isDirty, isSubmitting }
  } = useForm<ArticleCreateSchemaType>({
    resolver: zodResolver(articleCreateSchema),
    defaultValues: initialValues
  })

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages)

  const { addMessage } = useContext(ToastMessageContext)

  const keyword = useWatch({ control, name: 'keyword' })

  const [keywordOpen, setKeywordOpen] = useState(true)
  const [outlineOpen, setOutlineOpen] = useState(true)
  const [bodyOpen, setBodyOpen] = useState(true)
  const [seoOpen, setSeoOpen] = useState(true)

  const applyGeneratedArticle = (article: GeneratedArticle) => {
    const applied = applyGeneratedArticleToSchema(getValues(), article)
    setValue('title', applied.title, { shouldDirty: true })
    setValue('pageTitle', applied.pageTitle, { shouldDirty: true })
    setValue('metaDescription', applied.metaDescription, { shouldDirty: true })
    setValue('bodyHtml', applied.bodyHtml, { shouldDirty: true })
    setValue('bodyHtmlBottom', applied.bodyHtmlBottom, { shouldDirty: true })
    setValue('aiUsed', applied.aiUsed, { shouldDirty: true })
  }

  const { trigger: createArticle, isMutating: saving } = useCreateArticleMutation({
    onSuccess: (res) => {
      addMessage({ text: '記事を作成しました', type: MessageType.SUCCESS })
      onCreated(res.article_proposal_id)
    },
    onError: () => addMessage({ text: '記事の作成に失敗しました', type: MessageType.CRITICAL })
  })

  const handleSave = handleSubmit((data) =>
    createArticle(convertSchemaToCreateVars(siteId, data, sourceActionId)).catch(() => undefined)
  )

  return (
    <>
      {isDirty && (
        <TopBar
          status={TopBarStatus.CHANGE}
          captionText={'未反映の変更があります'}
          onCancel={() => reset(initialValues)}
          loading={isSubmitting}
          onClick={handleSave}
          withSide
        />
      )}
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />

      <ContentSideFrame>
        <Content type={ContentType.TWO_COLUMNS_LEFT}>
          <ArticleCreateFormKeyword
            siteId={siteId}
            keyword={keyword}
            register={register}
            setValue={setValue}
            getValues={getValues}
            errors={errors}
            isOpening={keywordOpen}
            onToggleOpen={() => setKeywordOpen((v) => !v)}
            onOutlineGenerated={() => setOutlineOpen(true)}
            onArticleAutoGenerated={(article) => {
              applyGeneratedArticle(article)
              setKeywordOpen(false)
              setOutlineOpen(false)
              setBodyOpen(true)
              setSeoOpen(true)
            }}
            onSkip={() => {
              setKeywordOpen(false)
              setOutlineOpen(true)
            }}
            onSkipToBody={() => {
              setKeywordOpen(false)
              setOutlineOpen(false)
              setBodyOpen(true)
              setSeoOpen(true)
            }}
          />
          <ArticleCreateFormOutline
            siteId={siteId}
            register={register}
            control={control}
            setValue={setValue}
            getValues={getValues}
            errors={errors}
            isOpening={outlineOpen}
            onToggleOpen={() => setOutlineOpen((v) => !v)}
            onArticleGenerated={(article) => {
              applyGeneratedArticle(article)
              setOutlineOpen(false)
              setBodyOpen(true)
              setSeoOpen(true)
            }}
            onSkip={() => {
              setOutlineOpen(false)
              setBodyOpen(true)
              setSeoOpen(true)
            }}
          />
          <ArticleCreateFormBody
            register={register}
            control={control}
            errors={errors}
            isOpening={bodyOpen}
            onToggleOpen={() => setBodyOpen((v) => !v)}
          />
          <ArticleCreateFormSeo
            register={register}
            control={control}
            errors={errors}
            isOpening={seoOpen}
            onToggleOpen={() => setSeoOpen((v) => !v)}
          />
          <PageBottom>
            <Button
              color={ButtonColor.PRIMARY_BRAND}
              size='large'
              loading={saving}
              onClick={handleSave}
            >
              保存する
            </Button>
          </PageBottom>
        </Content>
        <Side follow>
          <ArticleCreateProgress />
        </Side>
      </ContentSideFrame>
    </>
  )
}

export default ArticleCreateForm
